import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TimePicker } from '@/components/ui/time-picker';
import {
  LanguageScrollTabs,
  type Language,
} from '@/components/translations';
import { Plus, Trash2, Clock, Calendar } from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────────────

/** Локализованная строка: { ru: "...", en: "...", uz: "..." } */
export type LocalizedString = Record<string, string>;

export interface ScheduleDayItem {
  time: string;
  title: LocalizedString;
}

export interface ScheduleDay {
  date: string;
  title: LocalizedString;
  description?: LocalizedString;
  items: ScheduleDayItem[];
}

interface ScheduleEditorProps {
  value: ScheduleDay[];
  onChange: (days: ScheduleDay[]) => void;
  minDate?: Date;
  maxDate?: Date;
  languages?: Language[];
  activeLanguage?: string;
  onLanguageChange?: (code: string) => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function createEmptyDay(): ScheduleDay {
  return {
    date: '',
    title: {},
    description: {},
    items: [],
  };
}

function createEmptyItem(): ScheduleDayItem {
  return { time: '', title: {} };
}

function sortByDate(days: ScheduleDay[]): ScheduleDay[] {
  return [...days].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return a.date.localeCompare(b.date);
  });
}

function getLocalizedValue(value: LocalizedString | string | undefined, lang: string): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value[lang] ?? '';
}

function setLocalizedValue(current: LocalizedString | string | undefined, lang: string, text: string): LocalizedString {
  const base: LocalizedString = typeof current === 'string'
    ? { ru: current }
    : { ...(current ?? {}) };
  return { ...base, [lang]: text };
}

// ─── Component ──────────────────────────────────────────────────────────────

export function ScheduleEditor({
  value,
  onChange,
  minDate,
  maxDate,
  languages = [],
  activeLanguage: externalLang,
  onLanguageChange: externalOnLangChange,
}: ScheduleEditorProps) {
  const [internalLang, setInternalLang] = useState('ru');
  const lang = externalLang ?? internalLang;
  const setLang = externalOnLangChange ?? setInternalLang;

  const sorted = sortByDate(value);

  const addDay = () => {
    onChange([...value, createEmptyDay()]);
  };

  const removeDay = (dayIndex: number) => {
    const original = findOriginalIndex(dayIndex);
    onChange(value.filter((_, i) => i !== original));
  };

  const updateDay = (dayIndex: number, updates: Partial<ScheduleDay>) => {
    const original = findOriginalIndex(dayIndex);
    const next = [...value];
    next[original] = { ...next[original], ...updates };
    onChange(next);
  };

  const updateDayLocalizedField = (
    dayIndex: number,
    field: 'title' | 'description',
    text: string,
  ) => {
    const original = findOriginalIndex(dayIndex);
    const next = [...value];
    const day = next[original];
    next[original] = {
      ...day,
      [field]: setLocalizedValue(day[field], lang, text),
    };
    onChange(next);
  };

  const addItem = (dayIndex: number) => {
    const original = findOriginalIndex(dayIndex);
    const next = [...value];
    next[original] = {
      ...next[original],
      items: [...next[original].items, createEmptyItem()],
    };
    onChange(next);
  };

  const removeItem = (dayIndex: number, itemIndex: number) => {
    const original = findOriginalIndex(dayIndex);
    const next = [...value];
    next[original] = {
      ...next[original],
      items: next[original].items.filter((_, i) => i !== itemIndex),
    };
    onChange(next);
  };

  const updateItemTime = (dayIndex: number, itemIndex: number, time: string) => {
    const original = findOriginalIndex(dayIndex);
    const next = [...value];
    next[original] = {
      ...next[original],
      items: next[original].items.map((item, i) =>
        i === itemIndex ? { ...item, time } : item
      ),
    };
    onChange(next);
  };

  const updateItemTitle = (dayIndex: number, itemIndex: number, text: string) => {
    const original = findOriginalIndex(dayIndex);
    const next = [...value];
    next[original] = {
      ...next[original],
      items: next[original].items.map((item, i) =>
        i === itemIndex
          ? { ...item, title: setLocalizedValue(item.title, lang, text) }
          : item
      ),
    };
    onChange(next);
  };

  function findOriginalIndex(sortedIndex: number): number {
    const sortedDay = sorted[sortedIndex];
    return value.indexOf(sortedDay);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Расписание
          </CardTitle>
          <Button type="button" variant="outline" size="sm" onClick={addDay}>
            <Plus className="h-4 w-4 mr-1" />
            Добавить день
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Language Tabs */}
        {languages.length > 1 && (
          <LanguageScrollTabs
            languages={languages}
            activeLanguage={lang}
            onLanguageChange={setLang}
            size="sm"
            showLanguageSelector={false}
          />
        )}

        {sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            Расписание пока не добавлено. Нажмите «Добавить день» для начала.
          </p>
        ) : (
          <div className="space-y-4">
            {sorted.map((day, dayIdx) => (
              <div
                key={`day-${dayIdx}-${day.date}`}
                className="rounded-lg border p-4 space-y-3"
              >
                {/* Day header */}
                <div className="flex items-start gap-3">
                  <span className="text-xs text-muted-foreground mt-2.5 w-5 shrink-0 text-center font-medium">
                    {dayIdx + 1}
                  </span>
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Дата</Label>
                      <DatePicker
                        value={day.date ? new Date(day.date + 'T00:00:00') : null}
                        onChange={(date) => {
                          const dateStr = date
                            ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                            : '';
                          updateDay(dayIdx, { date: dateStr });
                        }}
                        placeholder="Выберите дату"
                        fromDate={minDate}
                        toDate={maxDate}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Название дня</Label>
                      <Input
                        value={getLocalizedValue(day.title, lang)}
                        onChange={(e) => updateDayLocalizedField(dayIdx, 'title', e.target.value)}
                        placeholder="Например: День 1 — Введение"
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 mt-6"
                    onClick={() => removeDay(dayIdx)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>

                {/* Day description */}
                <div className="ml-8 space-y-1.5">
                  <Label className="text-xs">Описание (необязательно)</Label>
                  <Textarea
                    value={getLocalizedValue(day.description, lang)}
                    onChange={(e) => updateDayLocalizedField(dayIdx, 'description', e.target.value)}
                    placeholder="Краткое описание дня..."
                    rows={2}
                    className="min-h-0"
                  />
                </div>

                {/* Time items */}
                <div className="ml-8 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      Пункты расписания
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => addItem(dayIdx)}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Добавить пункт
                    </Button>
                  </div>

                  {day.items.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-2 border rounded-md bg-muted/30">
                      Нет пунктов. Добавьте временные слоты.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {day.items.map((item, itemIdx) => (
                        <div
                          key={`item-${dayIdx}-${itemIdx}`}
                          className="flex items-center gap-2"
                        >
                          <TimePicker
                            value={item.time}
                            onChange={(time) => updateItemTime(dayIdx, itemIdx, time)}
                            className="w-28 shrink-0"
                          />
                          <Input
                            value={getLocalizedValue(item.title, lang)}
                            onChange={(e) => updateItemTitle(dayIdx, itemIdx, e.target.value)}
                            placeholder="Название пункта"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            onClick={() => removeItem(dayIdx, itemIdx)}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
